import { DEFAULT_LAYOUT, LAYOUTS, type StudioLayout } from "./layouts";
import type { BannerPreset } from "../profile/banner";

/**
 * What is on the broadcast, as an ordered list rather than a fixed shape.
 *
 * ## Why this replaced named slots
 *
 * The studio used to describe a frame as a `StudioLayout`: one camera, one
 * now-playing card, one name badge, each in a slot the layout chose. Four such
 * layouts shipped and they were good, but the model could not express "and
 * also, this image, here" - there was no room in it for a thing the host
 * brought. Adding a fifth named slot per idea is how a compositor ends up with
 * fifteen booleans and no way to put two of anything on screen.
 *
 * A scene is now a list of layers painted back to front. The four layouts still
 * exist and are still what a host starts from; they are *presets that emit a
 * list* rather than shapes the renderer knows about. Nothing a host has is
 * lost, and the renderer stopped needing to know what a "layout" is.
 *
 * ## Everything is normalised
 *
 * Boxes are fractions of the frame, not pixels, exactly as slots were. That is
 * what lets one `paint()` draw the host's small preview and the 1280x720 canvas
 * being encoded from the same scene - and it is what will let a drag on the
 * preview mean the same thing on the broadcast.
 *
 * ## Scenes are plural in the data before they are plural in the UI
 *
 * A `SceneBook` holds several named scenes and an active one, while the console
 * currently shows exactly one. That is deliberate: the shape somebody's saved
 * setup is stored in is the expensive thing to change later, and hosts will
 * want the intermission/be-right-back switching that every other broadcast tool
 * has. Building the container now costs a few lines; retrofitting it after
 * people have saved layouts costs a migration.
 */

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface BaseLayer {
  id: string;
  /** Where it sits, as fractions of the frame. */
  box: Box;
  /**
   * Whether it is painted.
   *
   * Hidden rather than removed, because the things a host toggles off - their
   * name, the now-playing card - are things they toggle back on, and a toggle
   * that destroys the layer loses wherever they had positioned it.
   */
  visible: boolean;
}

/** The host's camera. */
export interface CameraLayer extends BaseLayer {
  kind: "camera";
  /** Circular masks read as an inset; rectangles read as the frame. */
  shape: "rect" | "circle";
  /** Corner radius in design pixels, scaled with the frame when painted. */
  radius: number;
}

/** What is playing, in one of three treatments. */
export interface CardLayer extends BaseLayer {
  kind: "card";
  /**
   * `bar` is a lower third over the camera; `panel` is a column beside it;
   * `hero` is the artwork as the subject rather than a label on something else.
   */
  style: "bar" | "panel" | "hero";
}

/** Who is broadcasting. */
export interface BrandLayer extends BaseLayer {
  kind: "brand";
}

/** Anything the host brought with them. */
export interface ImageLayer extends BaseLayer {
  kind: "image";
  /**
   * Where it is served from.
   *
   * Must be a URL the compositor can load CORS-clean - see `images.ts`, which
   * is the only way images reach the canvas and the only reason
   * `captureStream()` does not throw.
   */
  src: string;
  /** `cover` fills and crops; `contain` fits the whole picture inside. */
  fit: "cover" | "contain";
  opacity: number;
}

export type Layer = CameraLayer | CardLayer | BrandLayer | ImageLayer;

export interface StudioScene {
  id: string;
  name: string;
  /**
   * Which preset this came from, or null once the host has moved something.
   *
   * Only the layout picker cares: it highlights the preset a scene still
   * matches, and shows nothing selected once the scene has been edited into
   * something that is no longer that preset. Claiming "Split" is selected under
   * a scene the host has rearranged is worse than claiming nothing.
   */
  presetId: string | null;
  background: BannerPreset | null;
  /** Painted first to last, so the first entry is furthest back. */
  layers: Layer[];
}

export interface SceneBook {
  /** Bumped when the stored shape changes in a way that needs migrating. */
  version: 1;
  scenes: StudioScene[];
  activeId: string;
}

/**
 * A new id.
 *
 * `randomUUID` needs a secure context, which localhost and production both are
 * - but a host on a bare-IP staging box is not, and a studio that throws on
 * open there would be a genuinely baffling thing to debug.
 */
export function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * The layers a preset stands for.
 *
 * Order is the z-order and it is the same one the old renderer hardcoded:
 * camera behind, then the card, then the name badge. Anything the host adds
 * goes on top of these by default, which is what "add" usually means - and can
 * be moved behind them once reordering exists.
 *
 * A preset with no camera slot still emits no camera layer rather than a hidden
 * one: "Artwork only" means the camera is not part of this shape, not that it
 * is switched off somewhere the host has to find.
 */
export function layersFromPreset(layout: StudioLayout): Layer[] {
  const layers: Layer[] = [];

  if (layout.camera) {
    layers.push({
      id: newId(),
      kind: "camera",
      box: { ...layout.camera },
      visible: true,
      shape: layout.cameraShape,
      /*
       * Square corners only when the camera is the whole frame. A rounded
       * full-bleed picture shows the background through four notches, which
       * reads as a rendering fault rather than a style.
       */
      radius: layout.camera.w === 1 && layout.camera.h === 1 ? 0 : 18,
    });
  }

  if (layout.card) {
    layers.push({
      id: newId(),
      kind: "card",
      box: { ...layout.card },
      visible: true,
      style: layout.cardStyle,
    });
  }

  if (layout.brand) {
    layers.push({
      id: newId(),
      kind: "brand",
      box: { ...layout.brand },
      visible: true,
    });
  }

  return layers;
}

/** A scene built from one of the shipped presets. */
export function sceneFromPreset(
  layout: StudioLayout,
  background: BannerPreset | null = null,
): StudioScene {
  return {
    id: newId(),
    name: layout.label,
    presetId: layout.id,
    background,
    layers: layersFromPreset(layout),
  };
}

/** What a host who has never opened the studio starts with. */
export function newSceneBook(): SceneBook {
  const scene = sceneFromPreset(DEFAULT_LAYOUT);

  return { version: 1, scenes: [scene], activeId: scene.id };
}

/**
 * The active scene, or the first one if the active id has gone stale.
 *
 * `newSceneBook()` is the fallback rather than a throw: a book with no scenes
 * should be impossible - `readSceneBook` rejects one - but the render loop is
 * not the place to find out it was not.
 */
export function activeScene(book: SceneBook): StudioScene {
  return (
    book.scenes.find((s) => s.id === book.activeId) ??
    book.scenes[0] ??
    newSceneBook().scenes[0]!
  );
}

/**
 * Whether the camera is the only thing on screen, filling it.
 *
 * ## Why this is asked of the layers rather than of a preset id
 *
 * It decides whether the broadcast can skip the canvas and publish the camera's
 * own track - which is not a cosmetic optimisation, it is what stops the
 * picture freezing when the host switches to another window. See `passthrough`
 * in `useStudio`.
 *
 * It used to be `layout.id === 'camera' && !showCard && !showBrand`, which
 * asked whether the host had picked a particular preset. The real question is
 * whether anything is being drawn over the camera, and now that a host can put
 * their own images on the frame those are two different questions: an overlay
 * on the camera preset would have kept the old test true and quietly stopped
 * the overlay reaching anybody.
 */
export function isBareCamera(layers: Layer[]): boolean {
  const visible = layers.filter((layer) => layer.visible);
  const [only] = visible;

  return (
    visible.length === 1 &&
    only !== undefined &&
    only.kind === "camera" &&
    only.shape === "rect" &&
    only.box.x === 0 &&
    only.box.y === 0 &&
    only.box.w === 1 &&
    only.box.h === 1
  );
}

/* -------------------------------------------------------------------------- */
/* Reading one back from storage                                               */
/* -------------------------------------------------------------------------- */

/**
 * A stored scene book, or nothing usable.
 *
 * Everything is checked, because what comes back was written by whatever build
 * the host last ran and it is handed straight to a render loop that runs thirty
 * times a second. A malformed box there is not an exception once, it is an
 * exception per frame.
 */
export function readSceneBook(value: unknown): SceneBook | null {
  if (typeof value !== "object" || value === null) return null;

  const book = value as Partial<SceneBook>;

  if (book.version !== 1 || !Array.isArray(book.scenes)) return null;

  const scenes = book.scenes
    .map(readScene)
    .filter((scene): scene is StudioScene => scene !== null);

  if (scenes.length === 0) return null;

  const active = scenes.some((s) => s.id === book.activeId)
    ? (book.activeId as string)
    : scenes[0]!.id;

  return { version: 1, scenes, activeId: active };
}

function readScene(value: unknown): StudioScene | null {
  if (typeof value !== "object" || value === null) return null;

  const scene = value as Partial<StudioScene>;

  if (typeof scene.id !== "string" || !Array.isArray(scene.layers)) return null;

  return {
    id: scene.id,
    name: typeof scene.name === "string" ? scene.name : "Scene",
    presetId:
      typeof scene.presetId === "string" &&
      LAYOUTS.some((l) => l.id === scene.presetId)
        ? scene.presetId
        : null,
    background: readBackground(scene.background),
    layers: scene.layers
      .map(readLayer)
      .filter((layer): layer is Layer => layer !== null),
  };
}

function readLayer(value: unknown): Layer | null {
  if (typeof value !== "object" || value === null) return null;

  const layer = value as Partial<Layer> & Record<string, unknown>;
  const box = readBox(layer.box);

  if (typeof layer.id !== "string" || box === null) return null;

  const base = { id: layer.id, box, visible: layer.visible !== false };

  switch (layer.kind) {
    case "camera":
      return {
        ...base,
        kind: "camera",
        shape: layer.shape === "circle" ? "circle" : "rect",
        radius:
          typeof layer.radius === "number" && Number.isFinite(layer.radius)
            ? layer.radius
            : 18,
      };

    case "card":
      return {
        ...base,
        kind: "card",
        style:
          layer.style === "panel" || layer.style === "hero"
            ? layer.style
            : "bar",
      };

    case "brand":
      return { ...base, kind: "brand" };

    case "image":
      return typeof layer.src === "string" && layer.src !== ""
        ? {
            ...base,
            kind: "image",
            src: layer.src,
            fit: layer.fit === "contain" ? "contain" : "cover",
            opacity:
              typeof layer.opacity === "number" &&
              layer.opacity >= 0 &&
              layer.opacity <= 1
                ? layer.opacity
                : 1,
          }
        : null;

    default:
      // A layer kind this build does not know about - written by a newer one,
      // most likely. Dropped rather than guessed at.
      return null;
  }
}

function readBox(value: unknown): Box | null {
  if (typeof value !== "object" || value === null) return null;

  const box = value as Partial<Box>;
  const ok = (n: unknown): n is number =>
    typeof n === "number" && Number.isFinite(n);

  if (!ok(box.x) || !ok(box.y) || !ok(box.w) || !ok(box.h)) return null;

  // A zero-width box is invisible and a negative one flips the draw. Neither is
  // something a host can see or fix, so they are not restored.
  if (box.w <= 0 || box.h <= 0) return null;

  return { x: box.x, y: box.y, w: box.w, h: box.h };
}

function readBackground(value: unknown): BannerPreset | null {
  if (typeof value !== "object" || value === null) return null;

  const preset = value as Partial<BannerPreset>;

  const usable =
    typeof preset.preset === "string" &&
    typeof preset.angle === "number" &&
    Number.isFinite(preset.angle) &&
    Array.isArray(preset.stops) &&
    preset.stops.length > 0 &&
    preset.stops.every((stop) => typeof stop === "string");

  return usable ? (preset as BannerPreset) : null;
}
